import React, { useState, useEffect, createContext, useContext } from 'react';
import ScorecardView, { PrecheckChip } from '../features/model_eval/ScorecardView';
import AiOfficePanel, { Step0Chip } from '../features/model_eval/AiOfficePanel';
import ValidationPanel from '../features/model_eval/ValidationPanel';

// Relative API Base path for Nginx reverse proxy
const API_BASE = '/api';

// Global provider model list surfaced by the Market Scanner. Hoisted to module scope
// so both the scanner and "Evaluate All" (seed-then-evaluate) can reuse the same set.
const MARKET_MODELS = [
        { id: "anthropic/claude-opus-5.5", name: "Claude Opus 5.5", source: "AWS Bedrock / Databricks", createdAt: "2026-09-22", aa_index: 98.4, latency: 340, pricing: 8.50 },
        { id: "amazon/nova-pro-v2", name: "Amazon Nova Pro V2", source: "AWS Bedrock Registry", createdAt: "2026-09-20", aa_index: 92.2, latency: 210, pricing: 0.80 },
        { id: "openai/gpt-6-luna", name: "GPT-6 Luna", source: "OpenAI Platform API", createdAt: "2026-09-20", aa_index: 97.2, latency: 290, pricing: 6.20 },
        { id: "spacexai/grok-4.7", name: "Grok 4.7", source: "xAI API Registry", createdAt: "2026-09-15", aa_index: 94.6, latency: 260, pricing: 3.50 },
        { id: "google/gemini-3.8-flash", name: "Gemini 3.8 Flash", source: "Google Cloud Vertex API", createdAt: "2026-09-12", aa_index: 93.1, latency: 140, pricing: 0.40 },
        { id: "meta-llama/llama-4-scout-70b", name: "Llama 4 Scout 70B", source: "Meta AI / HuggingFace", createdAt: "2026-08-05", aa_index: 89.1, latency: 240, pricing: 0.50 },
        { id: "deepseek/deepseek-v4.1-flash", name: "DeepSeek V4.1 Flash (China)", source: "DeepSeek Registry", createdAt: "2026-09-05", aa_index: 91.2, latency: 160, pricing: 0.20 },
        { id: "alibaba/qwen-3.8-max", name: "Qwen 3.8 Max (China)", source: "Alibaba Cloud Registry", createdAt: "2026-09-02", aa_index: 90.5, latency: 280, pricing: 1.80 },
        { id: "mistral/mistral-medium-3.5", name: "Mistral Medium 3.5 (France)", source: "Mistral Platform API", createdAt: "2026-07-20", aa_index: 88.0, latency: 210, pricing: 0.90 },
        { id: "cohere/command-r-plus-v2", name: "Cohere Command R+ V2 (Canada)", source: "Cohere API", createdAt: "2026-07-18", aa_index: 87.9, latency: 220, pricing: 1.10 },
        { id: "ai21/jamba-1.5-large", name: "AI21 Jamba 1.5 (Israel)", source: "AI21 Labs API", createdAt: "2026-07-12", aa_index: 86.8, latency: 250, pricing: 1.00 },
        { id: "xiaomi/mimo-v2.6-pro", name: "MiMo V2.6 Pro (China)", source: "HuggingFace Registry", createdAt: "2026-09-08", aa_index: 91.8, latency: 190, pricing: 0.15 },
        { id: "z-ai/glm-5.3-flash", name: "GLM 5.3 Flash (China)", source: "Zhipu AI Registry", createdAt: "2026-08-28", aa_index: 89.9, latency: 175, pricing: 0.25 },
        { id: "minimax/minimax-m3", name: "MiniMax M3 (China)", source: "MiniMax Registry", createdAt: "2026-07-15", aa_index: 87.5, latency: 230, pricing: 0.80 },
        { id: "sakana/evollm-jp-v2", name: "Sakana EvoLLM (Japan)", source: "Sakana AI Registry", createdAt: "2026-07-11", aa_index: 85.2, latency: 200, pricing: 0.40 },
        { id: "baidu/ernie-bot-5.0", name: "Ernie Bot 5.0 (China)", source: "Baidu Cloud Registry", createdAt: "2026-07-05", aa_index: 89.0, latency: 260, pricing: 1.20 },
        { id: "bytedance/doubao-pro", name: "Doubao Pro (China)", source: "ByteDance Volcengine", createdAt: "2026-07-01", aa_index: 88.2, latency: 190, pricing: 0.30 },
        { id: "openai/chatgpt-web-claude-web", name: "Consumer Chat Interface (chatgpt.com / claude.ai)", source: "Public Web Interface", createdAt: "2026-06-20", aa_index: 95.0, latency: 300, pricing: 0.00 },
        { id: "google/gemma-4-31b-it", name: "Gemma 4 31B Instruct", source: "Google Open Models", createdAt: "2026-08-10", aa_index: 88.5, latency: 180, pricing: 0.30 },
        { id: "nvidia/nemotron-3-ultra", name: "Nemotron 3 Ultra 120B", source: "NVIDIA NGC Registry", createdAt: "2026-07-10", aa_index: 90.1, latency: 220, pricing: 0.70 }
      ];


const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  // Authentication & RBAC State
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [role, setRole] = useState(localStorage.getItem('role') || '');
  const [privileges, setPrivileges] = useState(() => {
    try { return JSON.parse(localStorage.getItem('privileges') || '[]'); } catch { return []; }
  });
  const [allPrivileges, setAllPrivileges] = useState([]);
  // Admin panel state
  const [adminConfig, setAdminConfig] = useState(null);
  const [tfTestResult, setTfTestResult] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'viewer', privileges: [], active: true });
  // Privilege check: admins implicitly have everything.
  const can = (priv) => role === 'admin' || (privileges || []).includes(priv);
  const [username, setUsername] = useState(localStorage.getItem('username') || '');
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');

  // Sidebar Layout State
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Main EEA Sidebar Navigation State ('eval' | 'benchmarking' | 'tsa' | 'vending' | 'intake')
  const [mainNav, setMainNav] = useState('eval');

  // Sub-tabs under Global Model Evaluation
  const [activeTab, setActiveTab] = useState('echostar'); // 'echostar' | 'other' | 'criteria' | 'approved-cat' | 'market-scan' | 'suites' | 'eval-trace' | 'logs'
  const [searchQuery, setSearchQuery] = useState('');

  // Catalogs & Market Scanner State
  const [echostarCatalog, setEchostarCatalog] = useState([]);
  const [otherCatalog, setOtherCatalog] = useState([]);
  const [discoveredModels, setDiscoveredModels] = useState([]);
  // Market Scanner "new within" window (days). Dropdown: 1/2/3/7, default 1.
  const [marketScanDays, setMarketScanDays] = useState(1);
  
  // Test Suites State
  const [suitesCatalog, setSuitesCatalog] = useState([]);
  const [showAddSuiteModal, setShowAddSuiteModal] = useState(false);
  const [newSuiteData, setNewSuiteData] = useState({ suite_id: '', version: '1.0', prompt: '', expected_output: '' });

  // Single Evaluator State
  const [modelId, setModelId] = useState('us.anthropic.claude-3-7-sonnet-20250219-v1:0');
  const [scorecard, setScorecard] = useState(null);
  const [agentLogs, setAgentLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  // Governance Justification Modal
  const [selectedJustificationModel, setSelectedJustificationModel] = useState(null);
  // Row-level evaluation result (shown in a modal so evaluating from a table
  // does NOT switch tabs or depend on the Single Evaluator tab being active).
  const [evalResultModel, setEvalResultModel] = useState(null);
  const [evalResultLoading, setEvalResultLoading] = useState(false);
  // Per-model VM push/pull status keyed by "<mId>::<env>" -> 'pushing'|'pulling'|status string.
  const [vmStatus, setVmStatus] = useState({});

  // Mappings & UI States
  const [selectedVmMapping, setSelectedVmMapping] = useState({});
  // Per-model VM chosen on the Approved-by-Category tab for Push/Pull (overrides the recommended VM).
  const [pushVmPick, setPushVmPick] = useState({});
  // Per-model role/weight/tier used by Push: { [mId]: { role, weight, tier } }
  const [pushOpts, setPushOpts] = useState({});
  const [selectedDeployMode, setSelectedDeployMode] = useState({});
  const [expandedDetails, setExpandedDetails] = useState({});

  // Table sorting & column filter state (BenchLM-style table view)
  const [sortConfig, setSortConfig] = useState({ key: 'model_id', dir: 'asc' });
  const [colFilters, setColFilters] = useState({ model: '', governance: 'ALL', origin: 'ALL', platform: 'ALL', recommendation: 'ALL', domain: 'ALL', modality: 'ALL' });

  // Batch evaluation result / verification banner state
  const [batchResult, setBatchResult] = useState(null);

  // System Logs Inspector State
  const [selectedLogComponent, setSelectedLogComponent] = useState('backend');
  const [fetchedLogs, setFetchedLogs] = useState([]);

  useEffect(() => {
    if (token) {
      fetchCatalogs();
      fetchSuitesCatalog();
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'logs' && token && mainNav === 'eval') {
      fetchSystemLogs(selectedLogComponent);
    }
  }, [activeTab, selectedLogComponent, mainNav]);

  // Auth Handlers
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatusMsg('Authenticating with EEA Enterprise SSO...');
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUser, password: loginPass })
      });
      const data = await res.json();
      if (res.ok) {
        setToken(data.token);
        setRole(data.role);
        setUsername(data.username);
        setPrivileges(data.privileges || []);
        setAllPrivileges(data.all_privileges || []);
        localStorage.setItem('token', data.token);
        localStorage.setItem('role', data.role);
        localStorage.setItem('username', data.username);
        localStorage.setItem('privileges', JSON.stringify(data.privileges || []));
        setStatusMsg(`Logged in as ${data.username} (${data.role})`);
      } else {
        setStatusMsg(`Login failed: ${data.error || 'Invalid credentials'}`);
      }
    } catch (err) {
      setStatusMsg('API Connection Error during login.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setToken('');
    setRole('');
    setUsername('');
    setPrivileges([]);
    setStatusMsg('Logged out.');
  };

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  });

  // ── TrueFoundry "pull" (shared): the models enabled in the TF sandbox. Single
  // source of truth used to mark catalog/discovered models that already exist in
  // TrueFoundry — consumed by the Market Scanner, All Models, and EchoStar tabs.
  const [tfEnabled, setTfEnabled] = useState([]);       // enabled gateway fqns
  const [tfPulledAt, setTfPulledAt] = useState(null);   // last pull (epoch ms)
  const pullTruefoundryModels = async () => {
    try {
      const res = await fetch(`${API_BASE}/truefoundry-gateway-models`, { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(data.models)) { setTfEnabled(data.models); setTfPulledAt(Date.now()); }
    } catch (e) { /* non-fatal */ }
  };
  useEffect(() => { if (token) pullTruefoundryModels(); }, [token]);  // pull after login
  // normalized family-key match (mirror of backend resolve_target_model)
  const tfNormKey = (s) => (s || '').toLowerCase().replace(/\d{8}/g, '').replace(/[^a-z0-9]/g, '').replace(/v\d+$/, '');
  const tfEnabledMatch = (mid) => {
    if (!mid) return null;
    if (tfEnabled.includes(mid)) return mid;
    const key = tfNormKey(mid); if (!key) return null;
    return tfEnabled.find(f => {
      const lk = tfNormKey(f), mk = f.includes('/') ? tfNormKey(f.split('/').slice(1).join('/')) : lk;
      return key === lk || key === mk || lk.endsWith(key) || mk.endsWith(key);
    }) || null;
  };

  // Layered API Calls
  const fetchCatalogs = async () => {
    try {
      const res = await fetch(`${API_BASE}/catalog`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const rawList = Array.isArray(data) ? data : (data.items || []);
        const catalogList = rawList.map(normalizeScorecardItem);
        const echostar = catalogList.filter(item => item.compliance_status === "ECHOSTAR_APPROVED");
        const other = catalogList.filter(item => item.compliance_status !== "ECHOSTAR_APPROVED");
        setEchostarCatalog(echostar);
        setOtherCatalog(other);
      } else {
        setStatusMsg('Failed to load catalog from API.');
      }
    } catch (err) {
      setStatusMsg('API Connection Error loading catalog.');
    }
  };

  const fetchSuitesCatalog = async () => {
    try {
      const res = await fetch(`${API_BASE}/test-suite`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.items || []);
        setSuitesCatalog(list);
      }
    } catch (err) {
      console.error("Test suites fetch error:", err);
    }
  };

  // Delete an ENTIRE golden test suite (every prompt row sharing a suite_group).
  const deleteSuite = async (suiteGroup) => {
    if (!suiteGroup) return;
    setLoading(true);
    setStatusMsg(`Deleting test suite '${suiteGroup}'...`);
    try {
      const res = await fetch(`${API_BASE}/delete-suite`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ suite_group: suiteGroup }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && !data.error) {
        setStatusMsg(data.message || `Deleted suite '${suiteGroup}'.`);
        fetchSuitesCatalog();
      } else {
        setStatusMsg(`Delete failed: ${data.error || 'Server Error'}`);
      }
    } catch (err) {
      setStatusMsg('API error deleting suite.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemLogs = async (comp = 'backend') => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/logs?component=${comp}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setFetchedLogs(data.logs || []);
      } else {
        setFetchedLogs(["[ERROR] Failed to retrieve server logs."]);
      }
    } catch (err) {
      setFetchedLogs(["[ERROR] API Connection Error fetching server logs."]);
    } finally {
      setLoading(false);
    }
  };

  const scanArtificialAnalysisMarket = async (days = marketScanDays) => {
    const win = Number(days) || 1;
    const label = win === 1 ? 'last 1 day' : win === 7 ? 'last week' : `last ${win} days`;
    setLoading(true);
    setStatusMsg(`Scanning model releases from the ${label} (OpenRouter + Hugging Face)...`);
    try {
      // Scanner shows models released within the selected window (new_days=win).
      // Multi-source + keyless via the backend. Falls back to the static seed
      // list only if the live call truly fails (egress blocked).
      const res = await fetch(`${API_BASE}/market-scan?new_days=${win}&limit=60`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (res.ok && Array.isArray(data.models)) {
        setDiscoveredModels(data.models);
        let msg = `Live scan complete — ${data.models.length} model(s) released in the ${label} (OpenRouter + Hugging Face).`;
        // Backfill PREVIOUS releases into the Other catalog (missing only).
        try {
          const bf = await fetch(`${API_BASE}/market-backfill`, {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: '{}',
          });
          if (bf.ok) {
            const bd = await bf.json();
            if (bd && typeof bd.added === 'number') {
              msg += ` Added ${bd.added} previous release(s) to the Other catalog.`;
              if (bd.added > 0 && typeof fetchCatalogs === 'function') fetchCatalogs();
            }
          }
        } catch (e2) { /* backfill is best-effort; ignore */ }
        setStatusMsg(msg);
      } else {
        setDiscoveredModels(MARKET_MODELS);
        setStatusMsg('Live market scan unavailable — showing a cached sample list.');
      }
    } catch (err) {
      setDiscoveredModels(MARKET_MODELS);
      setStatusMsg('Live market scan failed (network/policy) — showing a cached sample list.');
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteToEchostarCatalog = async (mId, defaultVm = "echostar/smart-v1") => {
    if (!can('promote_other_to_echostar')) return setStatusMsg("'promote_other_to_echostar' privilege required to promote models.");
    
    const lowerId = mId.toLowerCase();
    const nonUsKeywords = ["deepseek", "qwen", "mistral", "cohere", "jamba", "glm", "ernie", "doubao", "minimax", "yi", "giga", "kandinsky", "chatgpt.com", "claude.ai"];
    // Match vendor names at a token boundary (e.g. 'qwen2.5', 'org/yi-34b'), not as raw substrings
    // inside unrelated IDs. Short names must also end at a non-letter. Policy PROHIBITED check still applies.
    const hitsVendor = (k) => new RegExp(`(^|[^a-z0-9])${k.replace(/[.]/g, '\\.')}${k.length <= 4 ? '(?![a-z])' : ''}`).test(lowerId);
    if (nonUsKeywords.some(hitsVendor)) {
      setStatusMsg(`PROMOTION BLOCKED: Model '${mId}' originates from a non-US company or high-risk country / unapproved web portal.`);
      return;
    }

    setLoading(true);
    const targetVm = selectedVmMapping[mId] || defaultVm;
    const mode = selectedDeployMode[mId] || "Managed Cloud API";

    setStatusMsg(`Promoting '${mId}' to Echostar Approved Catalog...`);
    try {
      const res = await fetch(`${API_BASE}/promote-model`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          model_id: mId,
          target_vm: targetVm,
          deployment_mode: mode
        })
      });
      const out = await res.json().catch(() => ({}));
      if (res.ok && !out.error) {
        setStatusMsg(`Successfully promoted '${mId}' to Echostar Approved Catalog (recommended VM: '${targetVm}'). Use the VM buttons on the Approved-by-Category tab to add it to TrueFoundry.`);
        fetchCatalogs();
      } else {
        setStatusMsg(`Promotion failed (${res.status}): ${out.error || 'Server Error'}`);
      }
    } catch (err) {
      setStatusMsg('API Error promoting model.');
    } finally {
      setLoading(false);
    }
  };

  // Staged TrueFoundry rollout: push an approved model's alias to np or prod
  // (nothing is pushed at promote time; every environment, sandbox included, is pushed manually).
  const handleTrueFoundrySync = async (mId, targetVm, environments) => {
    if (role !== 'admin') return setStatusMsg('Admin access required.');
    setLoading(true);
    setStatusMsg(`TrueFoundry: syncing '${mId}' -> '${targetVm}' to ${environments.join(', ')}...`);
    try {
      const res = await fetch(`${API_BASE}/truefoundry-sync`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ model_id: mId, target_vm: targetVm, environments })
      });
      const data = await res.json();
      if (res.ok && (data.status === 'SUCCESS' || data.status === 'COMPLETED_WITH_ERRORS')) {
        const summary = (data.results || [])
          .map(r => `${r.environment}:${r.status}`).join('  ');
        setStatusMsg(`TrueFoundry sync ${data.status} for '${mId}' -> ${environments.join(', ')} [ ${summary} ]`);
      } else if (res.ok && data.status === 'NOT_CONFIGURED') {
        setStatusMsg('TrueFoundry endpoint/token not configured on server — set TRUEFOUNDRY_API_BASE / TRUEFOUNDRY_API_TOKEN to enable live sync.');
      } else {
        setStatusMsg(`TrueFoundry sync failed: ${data.error || 'Server Error'}`);
      }
    } catch (err) {
      setStatusMsg('API Error during TrueFoundry sync.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoteToOtherCatalog = async (mId) => {
    if (role !== 'admin') return setStatusMsg('Admin access required to demote models.');
    setLoading(true);
    setStatusMsg(`Demoting model '${mId}' back to Other Models Catalog...`);
    try {
      const res = await fetch(`${API_BASE}/demote-model`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ model_id: mId })
      });
      if (res.ok) {
        setStatusMsg(`Model '${mId}' successfully demoted back to Other Models Catalog.`);
        fetchCatalogs();
      } else {
        const err = await res.json();
        setStatusMsg(`Demotion failed: ${err.error || 'Server Error'}`);
      }
    } catch (err) {
      setStatusMsg('API Error demoting model.');
    } finally {
      setLoading(false);
    }
  };

  const handleImportAndEvaluateFromMarket = async (targetModel, releaseDate = null) => {
    if (role !== 'admin') return setStatusMsg('Admin access required.');
    setLoading(true);
    setStatusMsg(`Importing model '${targetModel}' into Catalog and triggering evaluation...`);
    try {
      const res = await fetch(`${API_BASE}/evaluate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'import_market',
          model_id: targetModel,
          release_date: releaseDate,
          eval_type: 'benchmark'
        })
      });
      if (res.ok) {
        setStatusMsg(`Model '${targetModel}' imported to Other Catalog & evaluation queued!`);
        fetchCatalogs();
      } else {
        setStatusMsg(`Import failed.`);
      }
    } catch (err) {
      setStatusMsg('API Connection Error importing market model.');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToArtifactory = async (mId) => {
    if (role !== 'admin') return setStatusMsg('Admin access required.');
    setLoading(true);
    setStatusMsg(`Syncing model weights and HuggingFace artifacts for '${mId}' to JFrog Artifactory...`);
    try {
      const res = await fetch(`${API_BASE}/sync-artifactory`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ model_id: mId })
      });
      if (res.ok) {
        setStatusMsg(`Model weights for '${mId}' synced to Enterprise Artifactory Repository!`);
        fetchCatalogs();
      } else {
        setStatusMsg('Failed to sync model weights to Artifactory.');
      }
    } catch (err) {
      setStatusMsg('API Error syncing to Artifactory.');
    } finally {
      setLoading(false);
    }
  };

  const handleRunEvaluation = async (targetModel = modelId, releaseDate = null) => {
    if (role !== 'admin') {
      setStatusMsg('Permission Denied: Admin access required to execute evaluations.');
      return;
    }
    setLoading(true);
    setStatusMsg(`Evaluating model '${targetModel}'...`);
    setScorecard(null);
    setAgentLogs([
      { agent: 'Coordinator', action: `Evaluating '${targetModel}': checking Amazon Bedrock, then Google Vertex; looking up BenchLM and Artificial Analysis...` }
    ]);

    try {
      const res = await fetch(`${API_BASE}/evaluate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ model_id: targetModel, eval_type: 'benchmark', release_date: releaseDate })
      });
      const data = await res.json();
      
      if (res.ok && data.scorecard) {
        const row = normalizeScorecardItem(data.scorecard);
        const rsc = (row && row.scorecard) || {};
        const platformName = { bedrock: 'Amazon Bedrock', google_vertex: 'Google Vertex' };
        const pre = (row && row.scorecard_view && row.scorecard_view.precheck) || null;
        platformName.self_hosted = 'Self-hosted endpoint';
        const steps = (rsc.evaluation_attempts || []).map(a => ({ agent: platformName[a.platform] || a.platform, action: `${a.ok ? '✓' : '✗'} ${a.detail}` }));
        if (pre) steps.unshift({ agent: 'Pre-check', action: `${pre.status}: ${pre.message}` });
        const s0 = row && row.ai_office && row.ai_office.step0;
        if (s0) steps.unshift({ agent: 'Step 0 compliance', action: `${s0.status}: ${s0.message} (origin: ${(s0.gate_a || {}).label}; platform: ${(s0.gate_b || {}).status})` });
        const shc = row && row.self_host_check;
        if (shc) steps.push({ agent: 'Self-hosting (AWS GPU)', action: `${(shc.options || [])[0] ? `${shc.options[0].instance_type} (${shc.options[0].precision}) — ${shc.options[0].provision}` : 'no single-instance fit'} · deployed: ${(shc.deployed || {}).status || '?'}` });
        const lev = rsc.live_eval || {};
        if (rsc.evaluation_mode === 'LIVE') {
          steps.push({ agent: 'Live run', action: `${lev.suite_prompts || 0} golden test-suite prompt(s) + ${Math.max(0, (lev.prompts_sent || 0) - (lev.suite_prompts || 0))} safety prompt(s) · ${lev.calls_ok || 0}/${lev.prompts_sent || 0} calls OK · golden suite ${rsc.golden_suite_benchmark_score ?? '—'}` });
        }
        const pbr = publicBenchmarks(row);
        steps.push({ agent: 'Public benchmarks', action: `BenchLM ${pbr.benchlm ?? 'not found'} · Artificial Analysis ${pbr.aa ?? (pbr.status.artificial_analysis === 'no_api_key' ? 'no API key set in Admin' : 'not found')}` });
        const st = evalStatusOf(row);
        const resultText = { LIVE: 'Evaluated live.', PUBLIC_BENCHMARK_ONLY: "Not runnable on Amazon Bedrock or Google Vertex — showing public benchmarks only.", NO_DATA: NO_DATA_MESSAGE }[st] || 'Not evaluated.';
        steps.push({ agent: 'Result', action: resultText });
        setAgentLogs(prev => [...prev, ...steps]);
        setStatusMsg(`Evaluation finished for '${targetModel}': ${resultText}`);
        setScorecard(row);
        fetchCatalogs();
      } else {
        setStatusMsg(`Evaluation for '${targetModel}' returned no scorecard${data && data.error ? `: ${data.error}` : ''}. Nothing was saved or estimated.`);
        setScorecard(null);
      }
    } catch (err) {
      setStatusMsg('API Connection Error during evaluation.');
      setAgentLogs(prev => [...prev, { agent: 'Supervisor Agent', action: 'ERROR: API Connection Error during request.' }]);
    } finally {
      setLoading(false);
    }
  };

  // ---- TrueFoundry-VM Challenger registry --------------------------------
  // Add a model to the challenger arena (reason + criteria computed server-side
  // from the model vs the VM's current primary) or remove it. Used by the Other
  // tab ('Promote to Challenger'), the VM tab ('Move to Challenger') and the
  // Challenger tab ('Remove'). assign_vm privilege gates both.
  const handleChallengerAdd = async (mId, targetVm = null, source = null) => {
    if (!can('assign_vm')) return setStatusMsg("'assign_vm' privilege required to stage a challenger.");
    setStatusMsg(`Staging '${mId}' as a challenger...`);
    try {
      const res = await fetch(`${API_BASE}/challenger-add`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ model_id: mId, ...(targetVm ? { target_vm: targetVm } : {}), ...(source ? { source } : {}) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setStatusMsg(`Challenger add failed: ${data.error || 'server error'}`); return null; }
      setStatusMsg(`'${mId}' staged as a challenger for ${data.challenger_info?.target_vm || targetVm || 'its VM'}.`);
      fetchCatalogs();
      return data;
    } catch (e) { setStatusMsg('API error staging challenger.'); return null; }
  };

  const handleChallengerRemove = async (mId) => {
    if (!can('assign_vm')) return setStatusMsg("'assign_vm' privilege required.");
    setStatusMsg(`Removing '${mId}' from the challenger arena...`);
    try {
      const res = await fetch(`${API_BASE}/challenger-remove`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ model_id: mId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setStatusMsg(`Remove failed: ${data.error || 'server error'}`); return null; }
      setStatusMsg(`'${mId}' removed from the challenger arena.`);
      fetchCatalogs();
      return data;
    } catch (e) { setStatusMsg('API error removing challenger.'); return null; }
  };

  // Row-level Evaluate: runs one model and shows the result in a modal, WITHOUT
  // switching tabs. Robust to the backend's COMPLETED response shape.
  // Evaluate one model live. Starts a background job; the result popup shows the
  // council's live trace and swaps to the scorecard when the job finishes.
  const handleRowEvaluate = async (mId, releaseDate = null) => {
    if (!can('run_evaluation')) { setStatusMsg("'run_evaluation' privilege required to evaluate."); return; }
    setEvalResultLoading(true);
    setEvalResultModel({ model_id: mId, __loading: true });
    setStatusMsg(`Evaluating '${mId}'...`);
    try {
      const res = await fetch(`${API_BASE}/evaluate-live`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ model_id: mId, eval_type: 'benchmark', release_date: releaseDate, live: true })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.job_id) {
        // Live run started in the background (single judge or full council, per Admin › Evaluate grading): poll the trace; the popup swaps to the scorecard when done.
        setEvalResultModel({ model_id: mId, __loading: true, __job: data.job_id, __showCouncil: !!data.show_council });
      } else {
        // Older backends returned the scorecard inline (no job). Kept for compatibility; it
        // the scorecard directly. Show it immediately — no council trace panel.
        const normalized = normalizeScorecardItem(data.scorecard || data);
        const sc = (normalized && normalized.scorecard) || {};
        setEvalResultModel({ ...normalized });
        setEvalResultLoading(false);
        setStatusMsg(`Evaluation completed for '${mId}' — ${sc.evaluation_mode === 'LIVE' ? 'LIVE (real model calls)' : 'NOT EVALUATED'}. ${sc.evaluation_note || ''}`);
        fetchCatalogs();
      }
    } catch (err) {
      setStatusMsg(`Could not start evaluation: ${err.message}`);
      setEvalResultModel({ model_id: mId, __error: `Could not start evaluation: ${err.message}` });
      setEvalResultLoading(false);
    }
  };

  // Called by the trace panel when the evaluation job finishes.
  const handleEvalJobDone = (job) => {
    setEvalResultLoading(false);
    if (job.status !== 'DONE') {
      setStatusMsg(`Evaluation failed: ${job.error || 'unknown error'}`);
      setEvalResultModel(prev => ({ ...(prev || {}), __loading: false, __error: `Evaluation failed: ${job.error || 'unknown error'}` }));
      return;
    }
    const data = job.result || {};
    const normalized = normalizeScorecardItem(data.scorecard || data);
    const sc = (normalized && normalized.scorecard) || {};
    setEvalResultModel(prev => ({ ...normalized, __job: prev && prev.__job, __showCouncil: !!(prev && prev.__showCouncil) }));
    // scorecard_view.live.note already has the judge details removed when they're hidden in Admin.
    const liveNote = normalized && normalized.scorecard_view && normalized.scorecard_view.live && normalized.scorecard_view.live.note;
    setStatusMsg(`Evaluation completed for '${job.model_id}' — ${sc.evaluation_mode === 'LIVE' ? 'LIVE (real model calls)' : 'NOT EVALUATED'}. ${liveNote || sc.evaluation_note || ''}`);
    fetchCatalogs();
  };

  // Push a model into a VM group/alias in one environment (button-driven).
  const handleVmPush = async (mId, targetVm, environment, opts = {}) => {
    if (!can('push_truefoundry')) return setStatusMsg("'push_truefoundry' privilege required.");
    const key = `${mId}::${environment}`;
    setVmStatus(prev => ({ ...prev, [key]: 'pushing' }));
    setStatusMsg(`Pushing '${mId}' into ${targetVm} (${environment})...`);
    try {
      const res = await fetch(`${API_BASE}/truefoundry-push`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ action: 'push', model_id: mId, target_vm: targetVm, environments: [environment],
                               role: opts.role || 'primary',
                               ...(opts.weight !== undefined && opts.weight !== '' ? { weight: Number(opts.weight) } : {}),
                               ...(opts.tier ? { tier: opts.tier } : {}),
                               ...(opts.create_if_missing ? { create_if_missing: true } : {}) })
      });
      const data = await res.json();
      if (!res.ok) {
        setVmStatus(prev => ({ ...prev, [key]: 'ERROR' }));
        setStatusMsg(`Push failed (${res.status}) for '${mId}': ${data.error || data.message || 'server error'}`);
        return 'ERROR';
      }
      const r = (data.results || [])[0] || {};
      const st = r.status || data.status || 'DONE';
      setVmStatus(prev => ({ ...prev, [key]: st }));
      setStatusMsg(`Push ${st} — '${mId}' -> ${targetVm} (${environment})`
        + (r.weight !== undefined && r.weight !== null ? ` at ${r.weight}% weight` : '')
        + (r.message ? `: ${r.message}` : (data.message ? `: ${data.message}` : '')));
      // SUCCESS/CREATED (live) or NOT_CONFIGURED (pending) persist an EEA placement,
      // so refresh catalogs to surface the model in the VM tab.
      if (st === 'SUCCESS' || st === 'CREATED' || st === 'NOT_CONFIGURED') fetchCatalogs();
      return st;
    } catch (err) {
      setVmStatus(prev => ({ ...prev, [key]: 'ERROR' }));
      setStatusMsg('API Error during VM push.');
      return 'ERROR';
    }
  };

  // A/B flip: op = start | advance | rollback | complete
  const handleVmFlip = async (mId, environment, op, targetVm) => {
    if (!can('push_truefoundry')) return setStatusMsg("'push_truefoundry' privilege required.");
    if (op === 'complete' && !window.confirm(`Make '${mId}' the primary in ${environment}? The old primary stays as a 0% fallback.`)) return;
    if (op === 'rollback' && !window.confirm(`Roll back ${environment} to the weights from before the flip?`)) return;
    const key = `${mId}::${environment}`;
    setVmStatus(prev => ({ ...prev, [key]: 'flipping' }));
    if (op === 'compare') setStatusMsg(`Comparing '${mId}' with the current primary in ${environment}. This sends test-suite prompts to both models and can take a minute...`);
    try {
      const res = await fetch(`${API_BASE}/truefoundry-flip`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ op, model_id: mId, environment, ...(targetVm ? { target_vm: targetVm } : {}) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        setVmStatus(prev => ({ ...prev, [key]: 'ERROR' }));
        setStatusMsg(`Flip ${op} failed (${res.status}): ${data.error || 'server error'}`);
        return;
      }
      setVmStatus(prev => ({ ...prev, [key]: op === 'rollback' ? 'ROLLED BACK' : op === 'compare' ? ((data.comparison || {}).verdict || {}).verdict || 'DONE' : (data.flip ? `${data.flip.stage}%` : 'DONE') }));
      setStatusMsg(data.message || `Flip ${op} done.`);
      fetchCatalogs();
    } catch (err) {
      setVmStatus(prev => ({ ...prev, [key]: 'ERROR' }));
      setStatusMsg('API Error during flip.');
    }
  };

  // Pull (remove) a model from a VM group/alias in one environment.
  const handleVmPull = async (mId, targetVm, environment) => {
    if (!can('push_truefoundry')) return setStatusMsg("'push_truefoundry' privilege required.");
    const key = `${mId}::${environment}`;
    setVmStatus(prev => ({ ...prev, [key]: 'pulling' }));
    setStatusMsg(`Pulling '${mId}' out of ${targetVm} (${environment})...`);
    try {
      const res = await fetch(`${API_BASE}/truefoundry-pull`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ action: 'pull', model_id: mId, target_vm: targetVm, environments: [environment] })
      });
      const data = await res.json();
      if (!res.ok) {
        setVmStatus(prev => ({ ...prev, [key]: 'ERROR' }));
        setStatusMsg(`Pull failed (${res.status}) for '${mId}': ${data.error || data.message || 'server error'}`);
        return;
      }
      const r = (data.results || [])[0] || {};
      const st = r.status === 'SUCCESS' ? 'REMOVED' : (r.status || data.status || 'DONE');
      setVmStatus(prev => ({ ...prev, [key]: st }));
      setStatusMsg(`Pull ${st} — '${mId}' from ${targetVm} (${environment})`
        + (r.message ? `: ${r.message}` : (data.message ? `: ${data.message}` : '')));
      fetchCatalogs();
    } catch (err) {
      setVmStatus(prev => ({ ...prev, [key]: 'ERROR' }));
      setStatusMsg('API Error during VM pull.');
    }
  };

  // ---- Admin: config + users ----
  const fetchAdminConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}/config`, { headers: getAuthHeaders() });
      if (res.ok) setAdminConfig(await res.json());
    } catch (e) { setStatusMsg('Error loading config.'); }
  };

  const saveAdminConfig = async (section, obj) => {
    try {
      const res = await fetch(`${API_BASE}/config`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ [section]: obj })
      });
      const data = await res.json();
      setStatusMsg(res.ok ? `Config '${section}' saved.` : `Save failed: ${data.error || 'error'}`);
      fetchAdminConfig();
    } catch (e) { setStatusMsg('Error saving config.'); }
  };

  const handleTfTest = async () => {
    setTfTestResult({ __loading: true });
    setStatusMsg('Testing TrueFoundry connection...');
    try {
      const res = await fetch(`${API_BASE}/truefoundry-test`, { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      setTfTestResult(data);
      setStatusMsg(`TF test: ${data.ok ? 'OK' : 'FAILED'} (status ${data.status})`);
    } catch (e) {
      setTfTestResult({ ok: false, status: 'ERROR', message: String(e) });
      setStatusMsg('TF test request failed.');
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const res = await fetch(`${API_BASE}/users`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAdminUsers(data.users || []);
        if (data.all_privileges) setAllPrivileges(data.all_privileges);
      } else if (res.status === 403) {
        setStatusMsg('You lack the manage_users privilege.');
      }
    } catch (e) { setStatusMsg('Error loading users.'); }
  };

  const saveUser = async (u) => {
    try {
      const res = await fetch(`${API_BASE}/users`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(u)
      });
      const data = await res.json();
      setStatusMsg(res.ok && data.status === 'SUCCESS' ? `User '${u.username}' saved.` : `Save failed: ${data.message || data.error || 'error'}`);
      fetchAdminUsers();
      setNewUser({ username: '', password: '', role: 'viewer', privileges: [], active: true });
    } catch (e) { setStatusMsg('Error saving user.'); }
  };

  const deleteUser = async (uname) => {
    try {
      const res = await fetch(`${API_BASE}/users/${encodeURIComponent(uname)}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      setStatusMsg(res.ok ? `User '${uname}' deleted.` : 'Delete failed.');
      fetchAdminUsers();
    } catch (e) { setStatusMsg('Error deleting user.'); }
  };

  // Count how many models in a catalog list actually carry a real golden-suite score.
  const countEvaluated = (list) => list.filter(item => {
    const v = item.scorecard && item.scorecard.golden_suite_benchmark_score;
    return isMeasuredMetric(item, 'golden_suite_benchmark_score') && v !== undefined && v !== null && v !== '';
  }).length;

  const handleEvaluateAllModels = async () => {
    if (role !== 'admin') return setStatusMsg('Admin access required.');
    setLoading(true);
    setBatchResult(null);
    try {
      // Step 1: Seed the full global model list into the Other catalog first, so
      // "Evaluate All" fills the Other tab even if the user never clicked Import.
      // bulk_seed is idempotent — it skips models already present (won't clobber promotions).
      setStatusMsg('Seeding global market models into catalog (idempotent)...');
      let seededCount = 0;
      try {
        const seedRes = await fetch(`${API_BASE}/evaluate`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ action: 'bulk_seed', models: MARKET_MODELS })
        });
        if (seedRes.ok) {
          const seedData = await seedRes.json();
          seededCount = seedData.seeded_count || 0;
        }
      } catch (e) { /* seeding best-effort; batch still runs on existing rows */ }

      // Step 2: Run the synchronous batch evaluation across ALL catalog models.
      setStatusMsg(
        `${seededCount > 0 ? `Seeded ${seededCount} new models. ` : ''}` +
        'Running synchronous batch evaluation across ALL catalog models...'
      );
      // Batch scoring 600+ models can take minutes — give it a generous timeout
      // via AbortController so the browser doesn't kill the request prematurely.
      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), 300000); // 5 min
      let res, data;
      try {
        res = await fetch(`${API_BASE}/evaluate`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ model_id: 'all', eval_type: 'benchmark' }),
          signal: ctrl.signal,
        });
        data = await res.json();
      } catch (e) {
        clearTimeout(to);
        if (e.name === 'AbortError') {
          setStatusMsg('Batch is still running server-side (request took >5 min). Refreshing catalog — scores will appear as they persist.');
          fetchCatalogs();
          return;
        }
        throw e;
      }
      clearTimeout(to);

      if (res.ok && (data.status === 'COMPLETED' || data.status === 'COMPLETED_WITH_ERRORS')) {
        // Re-fetch the catalog and independently verify the scorecards landed.
        let verifiedEcho = 0, verifiedOther = 0, totalRows = 0;
        try {
          const cres = await fetch(`${API_BASE}/catalog`, { headers: getAuthHeaders() });
          if (cres.ok) {
            const cdata = await cres.json();
            const list = Array.isArray(cdata) ? cdata : (cdata.items || []);
            totalRows = list.length;
            verifiedEcho = countEvaluated(list.filter(i => i.compliance_status === 'ECHOSTAR_APPROVED'));
            verifiedOther = countEvaluated(list.filter(i => i.compliance_status !== 'ECHOSTAR_APPROVED'));
          }
        } catch (e) { /* verification fetch best-effort */ }

        const verifiedTotal = verifiedEcho + verifiedOther;
        const claimed = data.evaluated_count || 0;
        const reconciled = verifiedTotal >= claimed && claimed > 0;

        setBatchResult({
          ...data,
          verified_total: verifiedTotal,
          verified_rows: totalRows,
          reconciled,
        });
        setStatusMsg(
          `Batch ${data.status === 'COMPLETED' ? 'completed' : 'completed with errors'}: ` +
          `${claimed}/${data.total_models} evaluated` +
          (data.failed_count ? `, ${data.failed_count} failed` : '') +
          `. Verified ${verifiedTotal} scorecards persisted (batch ${data.batch_id}).`
        );
        fetchCatalogs();
      } else if (res.ok) {
        // Older/queued-style response fallback
        setBatchResult(null);
        setStatusMsg(`Batch response: ${data.message || 'dispatched'} (status: ${data.status || 'unknown'})`);
        fetchCatalogs();
      } else {
        setStatusMsg(`Batch evaluation failed: ${data.error || 'Server Error'}`);
      }
    } catch (err) {
      setStatusMsg('API Connection Error requesting batch evaluation.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoldenSuite = async (e) => {
    e.preventDefault();
    if (role !== 'admin') return setStatusMsg('Admin access required.');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/evaluate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'add_suite',
          suite_id: newSuiteData.suite_id || 'Golden_Suite_Custom',
          version: newSuiteData.version || '1.0',
          prompt: newSuiteData.prompt,
          expected_output: newSuiteData.expected_output
        })
      });
      if (res.ok) {
        setStatusMsg(`Golden Test Suite '${newSuiteData.suite_id}' added successfully!`);
        setShowAddSuiteModal(false);
        setNewSuiteData({ suite_id: '', version: '1.0', prompt: '', expected_output: '' });
        fetchSuitesCatalog();
      }
    } catch (err) {
      setStatusMsg('Error adding test suite.');
    } finally {
      setLoading(false);
    }
  };

  const toggleDetails = (mId) => {
    setExpandedDetails(prev => ({ ...prev, [mId]: !prev[mId] }));
  };

  const filterBySearch = (list) => list.filter(item => (item.model_id || item.id || '').toLowerCase().includes(searchQuery.toLowerCase()));

  const filteredEchostar = filterBySearch(echostarCatalog);
  const filteredOther = filterBySearch(otherCatalog);
  const filteredDiscovered = filterBySearch(discoveredModels);
  const filteredSuites = suitesCatalog.filter(s => (s.version || '').toLowerCase().includes(searchQuery.toLowerCase()) || (s.suite_id || '').toLowerCase().includes(searchQuery.toLowerCase()));

  const getGovernanceAnalysis = (item) => {
    const mId = (item.model_id || item.id || '').toLowerCase();
    
    if (mId.includes('chatgpt.com') || mId.includes('claude.ai')) {
      return {
        recommendation: "PROHIBITED",
        badgeColor: "#dc3545",
        statusLabel: "PROHIBITED_UNAPPROVED_WEB_INTERFACE",
        ruleTitle: "Rule 4/5: Prohibited Consumer Web Portal / Website Hosting",
        why: "Currently there is no enterprise agreement with OpenAI or Anthropic in place that permits the use of consumer web chat portals (chatgpt.com / claude.ai). Data submitted via web UI is unmonitored."
      };
    }

    if (mId.includes('deepseek') || mId.includes('qwen') || mId.includes('ernie') || mId.includes('doubao') || mId.includes('minimax') || mId.includes('glm') || mId.includes('yi') || mId.includes('kandinsky') || mId.includes('gigachat') || mId.includes('yandex')) {
      return {
        recommendation: "PROHIBITED",
        badgeColor: "#dc3545",
        statusLabel: "PROHIBITED_HIGH_RISK_COUNTRY",
        ruleTitle: "Rule 7: Prohibited Origin from High-Risk Countries (China / Russia)",
        why: "Foreign laws require companies to cooperate with state intelligence agencies. High-risk country models pose intentional backdoor risks, code vulnerabilities, and US sanctions compliance issues."
      };
    }

    if (mId.includes('mistral') || mId.includes('cohere') || mId.includes('jamba') || mId.includes('aleph') || mId.includes('sarvam') || mId.includes('evollm') || mId.includes('allam') || mId.includes('hyperclova') || mId.includes('falcon')) {
      return {
        recommendation: "PROHIBITED",
        badgeColor: "#dc3545",
        statusLabel: "PROHIBITED_NON_US_ORIGIN",
        ruleTitle: "Rule 6: Prohibited Origin from Non-US Providers",
        why: "Adequate US-based alternatives are available. Using non-US hosted or developed models exposes EchoStar to international compliance risks, data exposure, and government supply-chain disruptions."
      };
    }

    if (mId.includes('anthropic') && (mId.includes('bedrock') || mId.includes('databricks') || mId.includes('claude'))) {
      return {
        recommendation: "APPROVED",
        badgeColor: "#28a745",
        statusLabel: "ECHOSTAR_APPROVED",
        ruleTitle: "Rule 1: Approved Anthropic Models on AWS Bedrock & Databricks",
        why: "Models hosted on Bedrock and Databricks do NOT train on EchoStar data. Terms reviewed and approved by AI Office."
      };
    }

    if (mId.includes('amazon') || mId.includes('nova') || mId.includes('titan')) {
      return {
        recommendation: "APPROVED",
        badgeColor: "#28a745",
        statusLabel: "ECHOSTAR_APPROVED",
        ruleTitle: "Rule 2: Approved Amazon Models on AWS Bedrock",
        why: "Models hosted on Bedrock do NOT train on EchoStar data. Active MSA and enterprise terms in place with Amazon."
      };
    }

    return {
      recommendation: "WILL_REVIEW_IF_REQUESTED",
      badgeColor: "#ffc107",
      statusLabel: "REQUIRES_AI_OFFICE_REVIEW",
      ruleTitle: "Rule 3: Will Review if Requested (Other US Models)",
      why: "US proprietary or open-weights models can be reviewed upon submission to the AI Office. On-prem or low-cost cloud use cases may benefit from licensing review."
    };
  };

  // ---- BenchLM-style sortable / filterable table ----
  // Only MEASURED numbers are shown (mirrors backend/measurement.py). Old simulated
  // (hash-of-model-id) values still stored on legacy rows render as a dash.
  const isMeasuredMetric = (item, key) => {
    const sc = item.scorecard || {};
    const live = (sc.evaluation_mode || item.evaluation_mode) === 'LIVE';
    if (key === 'p95_latency_ms') return live || sc.latency_simulated === false;
    if (['golden_suite_benchmark_score', 'safety_compliance_pass_rate', 'internal_suite_score'].includes(key)) return live;
    if (key === 'public_benchmark_score') return sc.public_benchmarks_simulated === false || sc.aa_intelligence_index != null || sc.benchlm_score != null || (sc.quality_simulated === false && sc.public_benchmarks_simulated !== true);
    if (key === 'cost_per_1k_tokens_usd' || key === 'blended_usd_per_1m') return sc.cost_simulated === false;
    if (key === 'final_blended_score') return live || isMeasuredMetric(item, 'public_benchmark_score');
    return true;
  };

  const scNum = (item, key) => {
    const sc = item.scorecard || {};
    if (!isMeasuredMetric(item, key)) return null;
    let v = sc[key];
    if (v === undefined || v === null || v === '') return null;
    if (typeof v === 'string') {
      const n = parseFloat(v.replace('%', '').replace('$', ''));
      return isNaN(n) ? null : n;
    }
    return typeof v === 'number' ? v : null;
  };

  const fmtCell = (item, key, suffix = '', prefix = '') => {
    const n = scNum(item, key);
    if (n === null) return <span style={{ color: '#bbb' }}>&mdash;</span>;
    return <span>{prefix}{n}{suffix}</span>;
  };

  // Cost is STORED as USD per 1,000 tokens. Display it as USD per 1,000,000 tokens
  // (per-million) by scaling x1000. Keeps stored data untouched; only the view changes.
  const costPerMillion = (per1k) => {
    if (per1k === null || per1k === undefined || per1k === '') return null;
    const n = typeof per1k === 'string' ? parseFloat(per1k.replace('$', '')) : per1k;
    if (isNaN(n)) return null;
    return Math.round(n * 1000 * 100) / 100; // 2 decimals
  };

  // Normalize any catalog/eval item to a stable shape:
  //   { model_id, compliance_status, governance_status, virtual_model_recommendation,
  //     architecture_type, release_date, scorecard: { flat metrics... } }
  // Accepts backend wrappers where metrics may be nested under .scorecard, double-nested
  // (.scorecard.scorecard from the single-eval wrapper), or flattened at top level.
  const normalizeScorecardItem = (raw) => {
    if (!raw || typeof raw !== 'object') return raw;
    let item = { ...raw };
    // Unwrap a single-eval wrapper: { model_id, ..., scorecard: {golden...} } is already fine,
    // but the single path returns { ...wrapper, scorecard: {..metrics..} } sometimes double-wrapped.
    let sc = item.scorecard;
    if (sc && sc.scorecard && typeof sc.scorecard === 'object') {
      // double-nested: hoist the inner metrics up and merge wrapper meta
      const inner = sc.scorecard;
      item = { ...item, ...sc, scorecard: inner };
    }
    // If metrics live at top level (no scorecard object), gather them into scorecard.
    const METRIC_KEYS = ['golden_suite_benchmark_score','public_benchmark_score','accuracy_delta',
                         'p95_latency_ms','safety_compliance_pass_rate','cost_per_1k_tokens_usd',
                         'public_benchmarks_breakdown','evaluation_remarks'];
    if (!item.scorecard || typeof item.scorecard !== 'object') {
      const gathered = {};
      METRIC_KEYS.forEach(k => { if (item[k] !== undefined) gathered[k] = item[k]; });
      item.scorecard = gathered;
    }
    return item;
  };

  const fmtCostPerMCell = (item) => {
    const raw = isMeasuredMetric(item, 'cost_per_1k_tokens_usd') ? (item.scorecard || {}).cost_per_1k_tokens_usd : null;
    const perM = costPerMillion(raw);
    if (perM === null) return <span style={{ color: '#bbb' }}>&mdash;</span>;
    return <span>${perM.toFixed(2)}</span>;
  };

  // Backend (model_availability.py) is authoritative via deployment_options / architecture_type.
  // This keyword list is only a fallback for rows that were never classified; keep it in
  // step with model_availability.OPEN_WEIGHT_FAMILIES.
  const OPEN_WEIGHT_FALLBACK = ['llama', 'gemma', 'qwen', 'deepseek', 'mistral', 'mixtral', 'falcon', 'nemotron',
    'grok', 'gpt-oss', 'granite', 'olmo', 'glm', 'beam', 'muse-spark', 'musespark', 'muse spark'];
  const isSelfHostableModel = (item) =>
    (item.deployment_options && item.deployment_options.open_weights === true) ||
    (item.architecture_type || '').includes('Open Weights') ||
    OPEN_WEIGHT_FALLBACK.some(k => `${item.model_id || item.id || ''} ${item.display_name || ''}`.toLowerCase().includes(k));

  // (total B, active B) from deployment_options (backend) or the id/name ('816B-A42B', '70B').
  const paramSizeOf = (item) => {
    const d = item.deployment_options || {};
    let total = Number(d.total_params_b) || null;
    let active = Number(d.active_params_b) || null;
    if (!total) {
      const txt = `${(item.model_id || item.id || '').split('/').pop()} ${item.display_name || ''}`.toLowerCase().replace(/[\s_]+/g, '-');
      const moe = txt.match(/(?:^|[^a-z0-9.])(\d+(?:\.\d+)?)b-a(\d+(?:\.\d+)?)b(?![a-z])/);
      if (moe) { total = Number(moe[1]); active = Number(moe[2]); }
      else {
        const hits = [...txt.matchAll(/(?:^|[^a-z0-9.])(\d+(?:\.\d+)?)b(?![a-z])/g)].map(m => Number(m[1])).filter(n => n >= 0.1 && n <= 3000);
        if (hits.length) total = Math.max(...hits);
      }
    }
    return { total, active };
  };

  // Bedrock availability FOR THIS ACCOUNT, from the "Check Bedrock access" run (or a
  // real Evaluate call). Falls back to the scanner's "offered on Bedrock" flag.
  const BEDROCK_BLOCKED = ['SCP_DENY', 'RCP_DENY', 'IAM_DENY', 'BOUNDARY_DENY', 'SESSION_POLICY_DENY', 'NO_MODEL_ACCESS', 'USE_CASE_FORM'];
  const bedrockStateOf = (item) => {
    const a = (item && item.bedrock_access) || null;
    if (a && a.status) return a.status === 'ALLOWED' ? 'ALLOWED' : BEDROCK_BLOCKED.includes(a.status) ? 'BLOCKED' : a.status === 'NOT_FOUND' ? 'NOT_OFFERED' : 'UNKNOWN';
    return (item && (item.bedrock_available === true || item.bedrock_available === 'true')) ? 'OFFERED_UNCHECKED' : 'NONE';
  };
  const bedrockBadge = (item, size = '9px') => {
    const a = (item && item.bedrock_access) || {};
    const st = bedrockStateOf(item);
    const when = a.checked_at ? ` · checked ${a.checked_at}${a.region ? ` in ${a.region}` : ''}` : '';
    const pill = (txt, bg, fg, title) => (
      <span title={title} style={{ marginLeft: '4px', fontSize: size, fontWeight: 'bold', color: fg, background: bg, borderRadius: '8px', padding: '1px 6px', whiteSpace: 'nowrap' }}>{txt}</span>);
    if (st === 'ALLOWED') return pill('✅ Bedrock available', '#d4edda', '#155724', `This AWS account can call it on Bedrock${a.via ? ` (via ${a.via})` : ''}${when}`);
    if (st === 'BLOCKED') return pill(a.status === 'SCP_DENY' ? '⛔ Bedrock: blocked by SCP' : a.status === 'NO_MODEL_ACCESS' ? '🔒 Bedrock: access not granted' : a.status === 'USE_CASE_FORM' ? '📝 Bedrock: Anthropic use case form needed' : `⛔ Bedrock: ${a.label || a.status}`,
      ['NO_MODEL_ACCESS', 'USE_CASE_FORM'].includes(a.status) ? '#fff3cd' : '#f8d7da', ['NO_MODEL_ACCESS', 'USE_CASE_FORM'].includes(a.status) ? '#856404' : '#721c24', `${a.label || a.status}${a.message ? ` — ${a.message}` : ''}${when}`);
    if (st === 'UNKNOWN') return pill(`Bedrock: ${a.label || a.status}`, '#eee', '#555', `${a.message || ''}${when}`);
    if (st === 'OFFERED_UNCHECKED') return pill('Bedrock: offered (access not checked)', '#fff0d6', '#7a4b00', 'Listed on Bedrock in this region. Run "Check Bedrock access" to see whether this account may call it.');
    return null;
  };

  // Public benchmark scores that were actually MEASURED (BenchLM leaderboard and the
  // Artificial Analysis Intelligence Index). Never estimated.
  const publicBenchmarks = (item) => {
    const sc = (item && item.scorecard) || {};
    const num = (v) => { const n = typeof v === 'string' ? parseFloat(v) : v; return (typeof n === 'number' && !isNaN(n)) ? n : null; };
    const src = String((item && (item.source || item.discovered_source)) || sc.public_benchmark_source || '').toLowerCase();
    let bl = num(sc.benchlm_score);
    let aa = num(sc.aa_intelligence_index);
    if (aa === null && sc.quality_simulated === false) aa = num((sc.public_benchmarks_breakdown || {}).artificial_analysis_intelligence_index);
    const pub = sc.quality_simulated === false ? num(sc.public_benchmark_score) : null;
    if (pub !== null && bl === null && src.includes('benchlm') && !src.includes('artificial')) bl = pub;
    if (pub !== null && aa === null && src.includes('artificial') && !src.includes('benchlm')) aa = pub;
    const pbm = sc.public_benchmarks || {};
    return { benchlm: bl, aa, benchlmMatch: (pbm.benchlm || {}).matched, aaMatch: (pbm.artificial_analysis || {}).matched,
             status: pbm.status || {}, checkedAt: pbm.checked_at };
  };

  // What a scorecard can honestly show: LIVE (run on Bedrock / Google Vertex),
  // PUBLIC_BENCHMARK_ONLY, NO_DATA (tried: no platform and no public benchmark), or
  // NOT_RUN (never evaluated).
  const NO_DATA_MESSAGE = "This model can't be evaluated (it is not available on Amazon Bedrock or Google Vertex) and no public benchmark (BenchLM or Artificial Analysis) is available.";
  const evalStatusOf = (item) => {
    const sc = (item && item.scorecard) || {};
    if ((sc.evaluation_mode || (item && item.evaluation_mode)) === 'LIVE') return 'LIVE';
    const pbx = publicBenchmarks(item);
    if (pbx.benchlm !== null || pbx.aa !== null) return 'PUBLIC_BENCHMARK_ONLY';
    const tried = (sc.evaluation_attempts || []).length > 0 || sc.evaluation_status === 'NO_DATA';
    return tried ? 'NO_DATA' : 'NOT_RUN';
  };

  const renderModelTable = (list, isOtherCatalog) => {
    // AI Office recommendation per row (backend ai_office.decision, attached to every
    // catalog row). Order = sort order: best first.
    const REC_META = {
      APPROVED: { short: 'AI Office approved', icon: '🏛️', bg: '#d1f2eb', fg: '#0b5345', rank: -1 },
      READY: { short: 'Ready to propose', icon: '✅', bg: '#d4edda', fg: '#155724', rank: 0 },
      READY_WITH_REVIEW: { short: 'Ready for review', icon: '🔎', bg: '#e7f0ff', fg: '#084298', rank: 1 },
      BELOW_BAR: { short: 'Below the bar', icon: '📉', bg: '#fde8d8', fg: '#8a3b12', rank: 2 },
      NOT_READY: { short: 'Not ready', icon: '⏳', bg: '#fff3cd', fg: '#856404', rank: 3 },
      STOP: { short: 'Not eligible', icon: '⛔', bg: '#f8d7da', fg: '#721c24', rank: 4 },
    };
    const recOf = (item) => {
      const d = item && item.ai_office && item.ai_office.decision;
      return d && REC_META[d.verdict] ? d : null;
    };
    const recCounts = list.reduce((acc, item) => {
      const d = recOf(item);
      const k = d ? d.verdict : 'NONE';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    const recTitle = (d) => (d.rationale && d.rationale.length
      ? d.rationale.slice(0, 3).map(p => `${p.title}: ${p.text}`).join('\n\n') + '\n\n(Open the model for the full reasoning)'
      : [d.label + ' — ' + d.meaning,
      ...((d.next_actions || []).slice(0, 3).map(a => '→ ' + a)),
      ...((d.steps || []).filter(s => s.status === 'FAIL' || s.status === 'REVIEW').slice(0, 4)
        .map(s => `${s.status}: ${s.label} — ${s.why || ''}`))].join('\n'));
    // Domain / modality per row (backend model_domain.classify, attached to every row).
    const DOMAIN_LABELS = { general: 'General purpose', coding: 'Coding / software', cyber: 'Cybersecurity', safety: 'Safety / moderation',
      legal: 'Legal', finance: 'Finance', medical: 'Healthcare / medical', hr: 'HR / people', marketing: 'Marketing / content',
      customer_service: 'Customer service', telecom: 'Telecom / network', math_science: 'Math / science',
      translation: 'Translation / multilingual', retrieval: 'Search / retrieval', agentic: 'Agents / tool use' };
    const MODALITY_LABELS = { text: '📝 Text', vision: '👁️ Vision', image_gen: '🎨 Image generation', audio: '🎙️ Audio / speech in',
      speech_out: '🔊 Speech out', video: '🎬 Video', embedding: '🧭 Embeddings' };
    const FIT_META = { MATCH: ['✓ evaluated on its domain', '#155724'], GENERAL: ['✓ evaluated', '#155724'],
      MISMATCH: ['⚠ not evaluated on its domain', '#9a5a1b'], UNTESTED: ['not evaluated yet', '#999'] };
    const domOf = (item) => (item && item.domain && !item.domain.error ? item.domain : null);
    const domCounts = list.reduce((acc, item) => {
      const d = domOf(item);
      (d ? d.domains : ['general']).forEach(k => { acc[k] = (acc[k] || 0) + 1; });
      return acc;
    }, {});
    const modCounts = list.reduce((acc, item) => {
      const d = domOf(item);
      (d ? d.modalities : ['text']).forEach(k => { acc[k] = (acc[k] || 0) + 1; });
      return acc;
    }, {});
    const editDomain = async (item) => {
      const d = domOf(item) || {};
      const ans = typeof window !== 'undefined' && window.prompt(
        `Set the modality and domain for ${item.model_id || item.id}.\n\n` +
        `Format:  modalities | domains   (comma-separated; leave empty to clear the override)\n` +
        `Modalities: ${Object.keys(MODALITY_LABELS).join(', ')}\nDomains: ${Object.keys(DOMAIN_LABELS).join(', ')}`,
        `${(d.modalities || ['text']).join(', ')} | ${(d.domains || ['general']).join(', ')}`);
      if (ans === null || ans === false || ans === undefined) return;
      const [mods, doms] = String(ans).split('|').map(s => (s || '').split(',').map(x => x.trim().toLowerCase()).filter(Boolean));
      const body = (!mods || !mods.length) && (!doms || !doms.length)
        ? { model_id: item.model_id || item.id, clear: true }
        : { model_id: item.model_id || item.id, modalities: mods || [], domains: doms || [] };
      try {
        const r = await fetch(`${API_BASE}/model-domain`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body) });
        const res = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(res.error || `HTTP ${r.status}`);
        setStatusMsg(`Domain for ${body.model_id} ${body.clear ? 'reset to automatic detection' : 'saved'}.`);
        fetchCatalogs();
      } catch (e) { setStatusMsg(`Could not save the domain: ${e.message}`); }
    };
    // Tier classification (Tier 1 Frontier / Tier 2 SLM / Tier 3 Domain Fine-Tune).
    // Prefers an explicit backend tier_category; otherwise derives from the model's
    // quality, cost, architecture, and id — so EVERY model (existing or newly
    // ingested) carries a category with no data migration.
    const classifyTier = (item) => {
      if (item.tier_category) {
        const n = Number(String(item.tier_category).replace(/[^0-9]/g, ''));
        if (n >= 1 && n <= 3) return n;
      }
      const sc = item.scorecard || item;
      const q = Number(sc.golden_suite_benchmark_score ?? sc.public_benchmark_score ?? item.final_blended_score);
      const cost1k = Number(sc.cost_per_1k_tokens_usd);
      const id = (item.model_id || item.id || '').toLowerCase();
      const arch = (item.architecture_type || '').toLowerCase();
      if (['code', 'coder', 'codestral', 'med', 'bio', 'sql', 'legal', 'math', 'embed', 'finance'].some(k => id.includes(k))) return 3;
      if (['mini', 'small', 'haiku', 'flash', 'lite', 'nano', 'tiny', 'phi', '-1b', '-2b', '-3b', '-7b', '-8b'].some(k => id.includes(k))) return 2;
      // Frontier-scale by size (>=100B total, or MoE >=20B active): open weights alone
      // does not make a model a domain fine-tune (Beam 501B/A23B, Muse Spark 816B/A42B).
      const ps = paramSizeOf(item);
      if ((ps.total && ps.total >= 100) || (ps.active && ps.active >= 20)) return 1;
      if (!isNaN(cost1k) && cost1k > 0 && cost1k <= 0.0005) return 2;
      if (!isNaN(q) && q >= 88) return 1;
      if (arch.includes('open weights')) return 3;
      return 1;
    };
    const TIER_META = {
      1: { label: 'Tier 1 · Frontier', title: 'Frontier / Complex Reasoning — high-stakes, nuanced analysis', bg: '#ede7ff', fg: '#5b34c7' },
      2: { label: 'Tier 2 · SLM', title: 'Small Language Model — simple classification/extraction/routing at ultra-low cost', bg: '#e0f2e9', fg: '#1b7a4b' },
      3: { label: 'Tier 3 · Domain', title: 'Domain Fine-Tune — specialized tasks (coding, medical, etc.)', bg: '#ffeedd', fg: '#9a5a1b' },
    };
    let rows = list.filter(item => {
      const mId = (item.model_id || item.id || '');
      if (colFilters.model && !mId.toLowerCase().includes(colFilters.model.toLowerCase())) return false;
      const gov = isOtherCatalog ? getGovernanceAnalysis(item).statusLabel : (item.governance_status || 'ACTIVE');
      if (colFilters.governance !== 'ALL' && gov !== colFilters.governance) return false;
      if (colFilters.origin === 'SELF_HOSTABLE' && !isSelfHostableModel(item)) return false;
      if (colFilters.origin === 'PROPRIETARY' && isSelfHostableModel(item)) return false;
      // Platform-availability filter (dynamic): which deployment platform a model
      // is available on. bedrock_available / google_available are set by the
      // scanner/backfill from the Bedrock (and future Vertex) source.
      const bstate = bedrockStateOf(item);
      const onBedrock = bstate === 'ALLOWED' || bstate === 'OFFERED_UNCHECKED';
      if (colFilters.platform === 'BEDROCK_ALLOWED' && bstate !== 'ALLOWED') return false;
      if (colFilters.platform === 'BEDROCK_BLOCKED' && bstate !== 'BLOCKED') return false;
      const onGoogle = item.google_available === true || item.google_available === 'true';
      if (colFilters.platform === 'BEDROCK' && !onBedrock) return false;
      if (colFilters.platform === 'GOOGLE' && !onGoogle) return false;
      if (colFilters.platform === 'EITHER' && !(onBedrock || onGoogle)) return false;
      if (colFilters.platform === 'NONE' && (onBedrock || onGoogle)) return false;
      if (colFilters.recommendation && colFilters.recommendation !== 'ALL') {
        const d = recOf(item);
        if ((d ? d.verdict : 'NONE') !== colFilters.recommendation) return false;
      }
      if (colFilters.domain && colFilters.domain !== 'ALL') {
        const d = domOf(item);
        if (!(d ? d.domains : ['general']).includes(colFilters.domain)) return false;
      }
      if (colFilters.modality && colFilters.modality !== 'ALL') {
        const d = domOf(item);
        if (!(d ? d.modalities : ['text']).includes(colFilters.modality)) return false;
      }
      return true;
    });

    const numericKeys = ['golden_suite_benchmark_score', 'final_blended_score', 'public_benchmark_score', 'p95_latency_ms', 'safety_compliance_pass_rate', 'cost_per_1k_tokens_usd'];
    rows = [...rows].sort((a, b) => {
      const { key, dir } = sortConfig;
      let av, bv;
      if (numericKeys.includes(key)) {
        av = scNum(a, key); bv = scNum(b, key);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
      } else if (key === 'release_date') {
        av = a.release_date || ''; bv = b.release_date || '';
      } else if (key === 'ai_office_rec') {
        const ra = recOf(a), rb = recOf(b);
        av = ra ? REC_META[ra.verdict].rank : 9; bv = rb ? REC_META[rb.verdict].rank : 9;
        if (av === bv) { av = (a.model_id || a.id || '').toLowerCase(); bv = (b.model_id || b.id || '').toLowerCase(); }
      } else if (key === 'domain') {
        const da = domOf(a), db = domOf(b);
        av = `${da ? da.label : 'zzz'} ${(a.model_id || a.id || '').toLowerCase()}`;
        bv = `${db ? db.label : 'zzz'} ${(b.model_id || b.id || '').toLowerCase()}`;
      } else {
        av = (a.model_id || a.id || '').toLowerCase();
        bv = (b.model_id || b.id || '').toLowerCase();
      }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });

    const setSort = (key) => setSortConfig(prev =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    const arrow = (key) => sortConfig.key === key ? (sortConfig.dir === 'asc' ? ' \u25b2' : ' \u25bc') : '';

    const th = (label, key, align = 'left') => (
      <th onClick={() => setSort(key)}
          style={{ padding: '10px 8px', textAlign: align, cursor: 'pointer', userSelect: 'none',
                   borderBottom: '2px solid #cbd5e0', whiteSpace: 'nowrap',
                   color: sortConfig.key === key ? '#0073bb' : '#2d3748', fontSize: '12px' }}>
        {label}{arrow(key)}
      </th>
    );

    const governanceOptions = Array.from(new Set(list.map(item =>
      isOtherCatalog ? getGovernanceAnalysis(item).statusLabel : (item.governance_status || 'ACTIVE'))));

    return (
      <div>
      {/* AI Office recommendation summary: click a chip to filter the table */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '8px', fontSize: '11px' }}>
        <span style={{ color: '#4a5568', fontWeight: 'bold' }}>AI Office recommendation:</span>
        {Object.entries(REC_META).map(([k, m]) => (
          <button key={k} onClick={() => setColFilters({ ...colFilters, recommendation: colFilters.recommendation === k ? 'ALL' : k })}
                  title={colFilters.recommendation === k ? 'Show all' : `Show only "${m.short}"`}
                  style={{ padding: '2px 9px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer',
                           background: m.bg, color: m.fg, border: colFilters.recommendation === k ? `2px solid ${m.fg}` : '1px solid transparent' }}>
            {m.icon} {m.short} ({recCounts[k] || 0})
          </button>
        ))}
        {recCounts.NONE ? <span style={{ color: '#999' }}>· {recCounts.NONE} not computed yet (refresh the catalog)</span> : null}
        {colFilters.recommendation !== 'ALL' && (
          <button onClick={() => setColFilters({ ...colFilters, recommendation: 'ALL' })}
                  style={{ padding: '2px 8px', fontSize: '11px', border: '1px solid #cbd5e0', background: '#fff', borderRadius: '10px', cursor: 'pointer' }}>✕ clear</button>
        )}
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '1300px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f7fafc' }}>
              {th('Model ID', 'model_id')}
              {th('Origin / Arch', 'model_id')}
              {th('Governance', 'model_id')}
              {th('AI Office', 'ai_office_rec')}
              {th('Domain / modality', 'domain')}
              {th('Golden %', 'golden_suite_benchmark_score', 'right')}
              {th('Final', 'final_blended_score', 'right')}
              {th('Public (vs leader)', 'public_benchmark_score', 'right')}
              {th('P95 ms', 'p95_latency_ms', 'right')}
              {th('Safety %', 'safety_compliance_pass_rate', 'right')}
              {th('$/1M', 'cost_per_1k_tokens_usd', 'right')}
              {th('Released', 'release_date')}
              <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #cbd5e0', fontSize: '12px' }}>Tier</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #cbd5e0', fontSize: '12px' }}>Actions</th>
            </tr>
            <tr style={{ backgroundColor: '#fff' }}>
              <td style={{ padding: '6px 8px' }}>
                <input value={colFilters.model} onChange={e => setColFilters({ ...colFilters, model: e.target.value })}
                       placeholder="filter model..." style={{ width: '130px', padding: '4px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '3px' }} />
              </td>
              <td style={{ padding: '6px 8px' }}>
                <select value={colFilters.origin} onChange={e => setColFilters({ ...colFilters, origin: e.target.value })}
                        style={{ padding: '4px', fontSize: '11px' }}>
                  <option value="ALL">All</option>
                  <option value="SELF_HOSTABLE">Self-Hostable</option>
                  <option value="PROPRIETARY">Proprietary</option>
                </select>
                <select value={colFilters.platform} onChange={e => setColFilters({ ...colFilters, platform: e.target.value })}
                        title="Filter by deployment-platform availability"
                        style={{ padding: '4px', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  <option value="ALL">Any platform</option>
                  <option value="BEDROCK_ALLOWED">✅ Bedrock available (my account)</option>
                  <option value="BEDROCK_BLOCKED">⛔ Bedrock blocked (SCP / IAM / access)</option>
                  <option value="BEDROCK">On Bedrock (available or not checked)</option>
                  <option value="GOOGLE">✓ On Google</option>
                  <option value="EITHER">Bedrock or Google</option>
                  <option value="NONE">Not on either</option>
                </select>
              </td>
              <td style={{ padding: '6px 8px' }}>
                <select value={colFilters.governance} onChange={e => setColFilters({ ...colFilters, governance: e.target.value })}
                        style={{ padding: '4px', fontSize: '11px', maxWidth: '150px' }}>
                  <option value="ALL">All</option>
                  {governanceOptions.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </td>
              <td style={{ padding: '6px 8px' }}>
                <select value={colFilters.recommendation || 'ALL'} onChange={e => setColFilters({ ...colFilters, recommendation: e.target.value })}
                        title="Filter by AI Office recommendation" style={{ padding: '4px', fontSize: '11px', maxWidth: '150px' }}>
                  <option value="ALL">All</option>
                  {Object.entries(REC_META).map(([k, m]) => <option key={k} value={k}>{m.icon} {m.short} ({recCounts[k] || 0})</option>)}
                  {recCounts.NONE ? <option value="NONE">Not computed ({recCounts.NONE})</option> : null}
                </select>
              </td>
              <td style={{ padding: '6px 8px' }}>
                <select value={colFilters.domain || 'ALL'} onChange={e => setColFilters({ ...colFilters, domain: e.target.value })}
                        title="Filter by domain" style={{ padding: '4px', fontSize: '11px', maxWidth: '150px', display: 'block' }}>
                  <option value="ALL">Any domain</option>
                  {Object.entries(DOMAIN_LABELS).filter(([k]) => domCounts[k]).map(([k, l]) => <option key={k} value={k}>{l} ({domCounts[k]})</option>)}
                </select>
                <select value={colFilters.modality || 'ALL'} onChange={e => setColFilters({ ...colFilters, modality: e.target.value })}
                        title="Filter by modality" style={{ padding: '4px', fontSize: '11px', maxWidth: '150px', marginTop: '4px', display: 'block' }}>
                  <option value="ALL">Any modality</option>
                  {Object.entries(MODALITY_LABELS).filter(([k]) => modCounts[k]).map(([k, l]) => <option key={k} value={k}>{l} ({modCounts[k]})</option>)}
                </select>
              </td>
              <td colSpan={9}></td>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={14} style={{ padding: '20px', textAlign: 'center', color: '#888' }}>No models match the current filters.</td></tr>
            )}
            {rows.map((item, idx) => {
              const mId = item.model_id || item.id;
              const gov = getGovernanceAnalysis(item);
              const isApproved = item.compliance_status === 'ECHOSTAR_APPROVED' || (!isOtherCatalog && gov.recommendation === 'APPROVED');
              const isProhibited = gov.recommendation === 'PROHIBITED';
              const selfHost = isSelfHostableModel(item);
              const arch = item.architecture_type || (selfHost ? 'Open Weights (Self-Hostable)' : 'Frontier Proprietary');
              const govLabel = isOtherCatalog ? gov.statusLabel : (item.governance_status || 'ACTIVE');
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td style={{ padding: '8px', fontWeight: 'bold', wordBreak: 'break-all', maxWidth: '220px' }}>
                    {mId}
                    {(() => {
                      const tfHit = tfEnabledMatch(mId);
                      return tfHit ? (
                        <div title={`Enabled in TrueFoundry sandbox as ${tfHit} — can be added to a VM`}
                             style={{ display: 'inline-block', marginTop: '3px', padding: '1px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: 'bold',
                                      backgroundColor: '#d4edda', color: '#155724', border: '1px solid #b7dfc3' }}>
                          ✅ In TrueFoundry
                        </div>
                      ) : null;
                    })()}
                  </td>
                  <td style={{ padding: '8px', color: '#555' }}>
                    {arch}
                    {(item.discovered_source || item.source || bedrockStateOf(item) !== 'NONE' || item.scorecard_view) && (
                      <div style={{ marginTop: '3px' }}>
                        {(item.discovered_source || item.source) ? (
                        <span title="Discovery source(s)" style={{ fontSize: '9px', fontWeight: 'bold', color: '#2b6cb0', background: '#ebf4ff', borderRadius: '8px', padding: '1px 6px', textTransform: 'uppercase' }}>
                          {item.discovered_source || item.source}
                        </span>) : null}
                        {bedrockBadge(item)}
                        <Step0Chip item={item} />
                        <PrecheckChip item={item} />
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold',
                                   backgroundColor: isProhibited ? '#f8d7da' : isApproved ? '#d4edda' : '#fff3cd',
                                   color: isProhibited ? '#721c24' : isApproved ? '#155724' : '#856404' }}>{govLabel}</span>
                  </td>
                  <td style={{ padding: '8px', maxWidth: '190px' }}>
                    {(() => {
                      const d = recOf(item);
                      if (!d) return <span style={{ color: '#bbb' }} title="Refresh the catalog to compute the AI Office recommendation">&mdash;</span>;
                      const m = REC_META[d.verdict];
                      const next = (d.next_actions || [])[0];
                      return (
                        <div title={recTitle(d)} style={{ cursor: 'help' }}>
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: m.bg, color: m.fg }}>
                            {m.icon} {m.short}
                          </span>
                          {next && <div style={{ fontSize: '9px', color: '#666', marginTop: '3px', lineHeight: 1.3 }}>→ {next.length > 70 ? next.slice(0, 68) + '…' : next}</div>}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '8px', maxWidth: '200px' }}>
                    {(() => {
                      const d = domOf(item);
                      if (!d) return <span style={{ color: '#bbb' }} title="Refresh the catalog to detect the domain">&mdash;</span>;
                      const fit = FIT_META[(d.fit || {}).status] || FIT_META.UNTESTED;
                      const ev = (d.evaluated_on || []).filter(g => g.avg != null);
                      const tip = [`Modalities: ${(d.modality_labels || []).join(', ')}`, `Domains: ${(d.domain_labels || []).join(', ')}`,
                        d.source === 'override' ? `Set on the catalog${d.override && d.override.by ? ` by ${d.override.by}` : ''}` : `Detected from: ${(d.evidence || []).join('; ')}`,
                        ev.length ? `Evaluated on: ${ev.map(g => `${g.group} ${g.avg}`).join(', ')}` : 'Not evaluated LIVE yet',
                        (d.fit || {}).why || ''].filter(Boolean).join('\n');
                      return (
                        <div title={tip} style={{ cursor: 'help' }}>
                          <div style={{ whiteSpace: 'nowrap' }}>
                            <span style={{ fontSize: '12px', marginRight: '3px' }}>{d.modality_icons}</span>
                            {(d.domain_labels || []).map(l => (
                              <span key={l} style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 'bold', marginRight: '3px',
                                                     backgroundColor: l === 'General purpose' ? '#edf2f7' : '#e6f6f4', color: l === 'General purpose' ? '#4a5568' : '#0f5c55' }}>{l}</span>
                            ))}
                            {d.source === 'override' && <span title="Set on the catalog" style={{ fontSize: '9px', color: '#2b6cb0' }}>✎</span>}
                          </div>
                          <div style={{ fontSize: '9px', color: fit[1], marginTop: '3px' }}>
                            {ev.length ? `Evaluated: ${ev.slice(0, 2).map(g => `${g.group} ${g.avg}`).join(', ')}${ev.length > 2 ? '…' : ''} · ` : ''}{fit[0]}
                          </div>
                          {can('promote_market_to_other') && (
                            <a onClick={() => editDomain(item)} style={{ fontSize: '9px', color: '#2b6cb0', cursor: 'pointer' }}>edit</a>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#084298' }}>{fmtCell(item, 'golden_suite_benchmark_score', '%')}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                    {(() => {
                      const f = scNum(item, 'final_blended_score');
                      if (f === null) return <span style={{ color: '#bbb' }}>&mdash;</span>;
                      const elig = item.approved_eligible === true || item.approved_eligible === 'true';
                      return <span style={{ color: elig ? '#155724' : '#084298' }}>{f}{elig ? ' ✓' : ''}</span>;
                    })()}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {(() => {
                      const sc0 = item.scorecard || {};
                      const raw = sc0.public_benchmark_score;
                      if (!isMeasuredMetric(item, 'public_benchmark_score') && raw != null && raw !== '' && sc0.quality_simulated === true)
                        return <span title="SIMULATED estimate — not measured, not used for ranking" style={{ color: '#999', fontStyle: 'italic' }}>{raw}%</span>;
                      // Measured public benchmarks are leaderboard scores, not percentages: show the
                      // raw number plus where it sits against the current leader (backend position).
                      const pos = (((item.ai_office || {}).decision || {}).public || {}).positions || [];
                      const p = pos.find(x => x.relative != null) || pos[0];
                      if (!p) return fmtCell(item, 'public_benchmark_score', '');
                      const tip = `${p.label} ${p.raw}` + (p.relative != null ? ` = ${p.relative}% of the leader${p.leader_name ? ` (${p.leader_name} ${p.leader})` : ''}` +
                        (p.percentile != null ? `, ${p.percentile}th percentile of ${p.n} models` : '') : ' — leaderboard position not known yet');
                      return (
                        <span title={tip} style={{ cursor: 'help' }}>
                          {p.raw}{p.relative != null && <span style={{ fontSize: '10px', color: '#555', marginLeft: '3px' }}>({Math.round(p.relative)}% of leader)</span>}
                        </span>
                      );
                    })()}
                    {(() => {
                      const sim = (item.scorecard || {}).quality_simulated;
                      if (sim === true) return <span title="Simulated estimate — not a measured benchmark" style={{ marginLeft: '4px', fontSize: '9px', color: '#9a5a1b' }}>sim</span>;
                      if (sim === false) return <span title="Measured benchmark (BenchLM / Artificial Analysis)" style={{ marginLeft: '4px', fontSize: '9px', color: '#1b7a4b' }}>real</span>;
                      return null;
                    })()}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{fmtCell(item, 'p95_latency_ms', ' ms')}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{fmtCell(item, 'safety_compliance_pass_rate', '%')}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{fmtCostPerMCell(item)}</td>
                  <td style={{ padding: '8px', whiteSpace: 'nowrap', color: '#555' }}>{item.release_date || <span style={{ color: '#bbb' }}>&mdash;</span>}</td>
                  <td style={{ padding: '8px' }}>
                    {(() => {
                      const tm = TIER_META[classifyTier(item)];
                      return <span title={tm.title} style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: tm.bg, color: tm.fg }}>{tm.label}</span>;
                    })()}
                  </td>
                  <td style={{ padding: '8px' }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                      <button onClick={() => setSelectedJustificationModel(item)} title="View justification"
                              style={{ padding: '4px 6px', fontSize: '10px', border: '1px solid #b6d4fe', background: '#eef6fc', color: '#084298', borderRadius: '3px', cursor: 'pointer' }}>Details</button>
                      {can('run_evaluation') && (
                        <button onClick={() => handleRowEvaluate(mId, item.release_date)} disabled={evalResultLoading} title="Run Golden Suite evaluation (shows result here, stays on this tab)"
                                style={{ padding: '4px 6px', fontSize: '10px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '3px', cursor: 'pointer' }}>Evaluate</button>
                      )}
                      {can('promote_other_to_echostar') && isOtherCatalog && (
                        <button onClick={() => handlePromoteToEchostarCatalog(mId, (item.virtual_model_recommendation || {}).recommended_vm)}
                                disabled={loading || isProhibited} title={isProhibited ? 'Prohibited by policy' : 'Promote to EchoStar catalog'}
                                style={{ padding: '4px 6px', fontSize: '10px', border: 'none', background: isProhibited ? '#6c757d' : '#28a745', color: '#fff', borderRadius: '3px', cursor: isProhibited ? 'not-allowed' : 'pointer' }}>
                          {isProhibited ? 'Blocked' : 'Promote'}</button>
                      )}
                      {can('assign_vm') && !isProhibited && (
                        <button onClick={() => handleChallengerAdd(mId, item.assigned_virtual_model || (item.virtual_model_recommendation || {}).recommended_vm)}
                                disabled={loading} title="Stage as a challenger to replace the VM incumbent (records why + criteria). EchoStar-approved models are vetted and get a ranking bonus."
                                style={{ padding: '4px 6px', fontSize: '10px', border: 'none', background: '#fd7e14', color: '#fff', borderRadius: '3px', cursor: 'pointer' }}>
                          Promote to Challenger</button>
                      )}
                      {can('promote_other_to_echostar') && !isOtherCatalog && isApproved && (
                        <button onClick={() => handleDemoteToOtherCatalog(mId)} disabled={loading} title="Demote to Other Models catalog"
                                style={{ padding: '4px 6px', fontSize: '10px', border: 'none', background: '#fd7e14', color: '#fff', borderRadius: '3px', cursor: 'pointer' }}>Demote</button>
                      )}
                      {can('push_truefoundry') && selfHost && !isProhibited && (
                        <button onClick={() => handleSyncToArtifactory(mId)} disabled={loading} title="Push weights to self-hosted registry (JFrog Artifactory / vLLM)"
                                style={{ padding: '4px 6px', fontSize: '10px', border: 'none', background: '#17a2b8', color: '#fff', borderRadius: '3px', cursor: 'pointer' }}>Push Self-Hosted</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
    );
  };

  const renderModelScorecard = (item, isOtherCatalog = false) => {
    const govAnalysis = getGovernanceAnalysis(item);
    const isApproved = item.compliance_status === "ECHOSTAR_APPROVED" || (!isOtherCatalog && govAnalysis.recommendation === "APPROVED");
    const isProhibited = govAnalysis.recommendation === "PROHIBITED";
    const isDeprecated = item.is_deprecated || item.compliance_status === "DEPRECATED_MODEL";
    const vmRec = item.virtual_model_recommendation || {};
    const assignedVm = item.assigned_virtual_model || vmRec.recommended_vm || "echostar/smart-v1";
    const deployMode = item.deployment_mode || "Managed Cloud API";
    const releaseDate = item.release_date || "2026-01-01";
    const pb = (item.scorecard?.quality_simulated === false && item.scorecard?.public_benchmarks_breakdown) || {};
    const isExpanded = !!expandedDetails[item.model_id];
    const isSelfHostable = (item.architecture_type || '').includes("Open Weights") || (item.model_id || '').toLowerCase().includes("llama") || (item.model_id || '').toLowerCase().includes("gemma");

    return (
      <div style={{
        border: isDeprecated ? '2px solid #fd7e14' : isApproved ? '2px solid #28a745' : isProhibited ? '2px solid #dc3545' : '2px solid #ffc107',
        borderRadius: '8px', padding: '16px', backgroundColor: isDeprecated ? '#fff8f0' : '#ffffff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h4 style={{ margin: 0, fontSize: '15px', wordBreak: 'break-all' }}>{item.model_id}</h4>
          <span style={{
            padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold',
            backgroundColor: isDeprecated ? '#ffe8cc' : isApproved ? '#d4edda' : isProhibited ? '#f8d7da' : '#fff3cd',
            color: isDeprecated ? '#d9480f' : isApproved ? '#155724' : isProhibited ? '#721c24' : '#856404'
          }}>
            {isOtherCatalog ? govAnalysis.statusLabel : (item.governance_status || 'ACTIVE')}
          </span>
        </div>

        {bedrockStateOf(item) !== 'NONE' && (
          <div style={{ fontSize: '12px', color: '#555', marginBottom: '6px' }}>
            <strong>Bedrock:</strong>{bedrockBadge(item, '11px')}
          </div>)}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#555', marginBottom: '10px' }}>
          <span><strong>Arch:</strong> {(() => {
            const d = item.deployment_options;
            if (!d) return item.architecture_type || (isSelfHostable ? 'Open Weights (Self-Hostable)' : 'Frontier Proprietary');
            const badge = (txt, bg, fg, title) => (
              <span key={txt} title={title} style={{ padding: '1px 7px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold', backgroundColor: bg, color: fg, marginRight: '4px' }}>{txt}</span>);
            return (
              <span>
                {d.frontier_api ? badge('Frontier API', '#e7f0ff', '#0b5cad', 'Available as a hosted API') : null}
                {d.open_weights ? badge('Open Weights (Self-Hostable)', '#e7f5ec', '#1b7a4b', d.evidence || '') : null}
                {d.open_weights && !d.open_weights_exact && (d.open_weight_repos || []).length > 0 ? (
                  <span style={{ display: 'block', fontSize: '10px', color: '#666', marginTop: '2px' }}>
                    Self-hostable releases: {(d.open_weight_repos || []).join(', ')} (this version is API-only)
                  </span>) : null}
              </span>);
          })()}</span>
          <span style={{ backgroundColor: '#eef6fc', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold', color: '#0073bb' }}>
            📅 Released: {releaseDate}
          </span>
        </div>

        <div style={{ backgroundColor: '#f0f4f8', borderLeft: '4px solid #0073bb', padding: '10px', borderRadius: '4px', marginBottom: '10px' }}>
          <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#0073bb' }}>TRUEFOUNDRY VIRTUAL ALIAS</span>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#004085', marginTop: '2px' }}>
            {isApproved ? `Active Target for: ${assignedVm}` : `Recommended: ${vmRec.recommended_vm || 'echostar/smart-v1'}`}
          </div>
        </div>

        <AiOfficePanel item={item} getAuthHeaders={getAuthHeaders}
          onUpdated={(updated) => {
            fetchCatalogs();
            if (updated && updated.model_id) setScorecard(prev => (prev && prev.model_id === updated.model_id) ? normalizeScorecardItem(updated) : prev);
          }} />
        <ScorecardView item={item} getAuthHeaders={getAuthHeaders} canRun={can('run_evaluation')} canLaunch={can('manage_config')}
          onUpdated={(updated) => {
            fetchCatalogs();
            if (updated && updated.model_id) setScorecard(prev => (prev && prev.model_id === updated.model_id) ? normalizeScorecardItem(updated) : prev);
          }} />
        <ValidationPanel item={item} getAuthHeaders={getAuthHeaders} canRun={can('run_evaluation')}
          onUpdated={() => fetchCatalogs()} />

        <div style={{ marginBottom: '8px' }}>
          <button
            onClick={() => setSelectedJustificationModel(item)}
            style={{ width: '100%', padding: '6px', backgroundColor: '#eef6fc', border: '1px solid #b6d4fe', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold', color: '#084298', textAlign: 'center' }}>
            🔍 View Approval Justification & Policy Rules
          </button>
        </div>

        <div style={{ marginBottom: '10px' }}>
          <button
            onClick={() => toggleDetails(item.model_id)}
            style={{ width: '100%', padding: '6px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', textAlign: 'left', fontWeight: 'bold', color: '#333' }}>
            {isExpanded ? '▼ Hide evaluation details' : '▶ 🔍 Evaluation details (platforms checked, benchmark sources)'}
          </button>

          {isExpanded && (() => {
            const sc = item.scorecard || {};
            const pbs = publicBenchmarks(item);
            const platformName = { bedrock: 'Amazon Bedrock', google_vertex: 'Google Vertex' };
            const atts = sc.evaluation_attempts || [];
            const stat = (k) => ({ ok: 'checked', no_api_key: 'no API key set in Admin > Sources', unreachable: 'source unreachable', unavailable: 'lookup unavailable' }[pbs.status[k]] || 'not checked yet');
            return (
              <div style={{ backgroundColor: '#fafafa', border: '1px solid #ddd', padding: '10px', borderRadius: '4px', marginTop: '6px', fontSize: '11px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#0073bb' }}>Platforms checked for a live run</div>
                {atts.length === 0 ? <div style={{ color: '#888' }}>Not checked yet — click Evaluate.</div> : atts.map((a, i) => (
                  <div key={i}>{a.ok ? '✓' : '✗'} <strong>{platformName[a.platform] || a.platform}</strong>: {a.detail}</div>))}
                <div style={{ fontWeight: 'bold', margin: '8px 0 4px', color: '#0073bb' }}>Public benchmark sources</div>
                <div>BenchLM: {pbs.benchlm !== null ? `${pbs.benchlm}${pbs.benchlmMatch ? ` (matched “${pbs.benchlmMatch}”)` : ''}` : `not found (${stat('benchlm')})`}</div>
                <div>Artificial Analysis: {pbs.aa !== null ? `${pbs.aa}${pbs.aaMatch ? ` (matched “${pbs.aaMatch}”)` : ''}` : `not found (${stat('artificial_analysis')})`}</div>
                {pbs.checkedAt ? <div style={{ color: '#888', marginTop: '4px' }}>Looked up {pbs.checkedAt}</div> : null}
              </div>);
          })()}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {isOtherCatalog && !isDeprecated && (
            <div style={{ backgroundColor: '#fafafa', padding: '10px', borderRadius: '4px', border: '1px dashed #ccc' }}>
              <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>🔄 TrueFoundry Virtual Alias Routing Target</div>
              <select
                value={selectedVmMapping[item.model_id] || item.assigned_virtual_model || vmRec.recommended_vm || "echostar/smart-v1"}
                onChange={(e) => setSelectedVmMapping({ ...selectedVmMapping, [item.model_id]: e.target.value })}
                style={{ width: '100%', padding: '6px', fontSize: '12px', marginBottom: '6px' }}>
                <option value="echostar/fast-v1">Alias: echostar/fast-v1 (Low Latency)</option>
                <option value="echostar/smart-v1">Alias: echostar/smart-v1 (Core Enterprise)</option>
                <option value="echostar/balanced-v1">Alias: echostar/balanced-v1 (Frontier Reasoning)</option>
              </select>

              {isSelfHostable && (
                <label style={{ fontSize: '11px', color: '#0d47a1', display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    disabled={role !== 'admin'}
                    checked={selectedDeployMode[item.model_id] === "Self-Hosted Private K8s (vLLM)" || deployMode.includes("Self-Hosted")}
                    onChange={(e) => setSelectedDeployMode({
                      ...selectedDeployMode,
                      [item.model_id]: e.target.checked ? "Self-Hosted Private K8s (vLLM)" : "Managed Cloud API"
                    })}
                  />
                  <strong>Deploy as Self-Hosted Model in Private K8s Cluster (vLLM)</strong>
                </label>
              )}

              {role === 'admin' && (
                <button
                  onClick={() => handlePromoteToEchostarCatalog(item.model_id, vmRec.recommended_vm)}
                  disabled={loading || isProhibited}
                  style={{ width: '100%', padding: '8px', backgroundColor: isProhibited ? '#6c757d' : '#28a745', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: isProhibited ? 'not-allowed' : 'pointer', fontSize: '12px' }}>
                  {isProhibited ? '⛔ Prohibited from Promotion' : '✅ Promote to Echostar Approved Catalog'}
                </button>
              )}
            </div>
          )}

          {!isOtherCatalog && isApproved && role === 'admin' && (
            <button
              onClick={() => handleDemoteToOtherCatalog(item.model_id)}
              disabled={loading}
              style={{ width: '100%', padding: '7px', backgroundColor: '#fd7e14', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px' }}>
              ⏬ Demote to Other Models Catalog
            </button>
          )}

          {isApproved && (
            <button
              onClick={() => handleSyncToArtifactory(item.model_id)}
              disabled={loading || role !== 'admin'}
              style={{ width: '100%', padding: '7px', backgroundColor: '#17a2b8', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px' }}>
              📦 Sync Weights to JFrog Artifactory ML Registry
            </button>
          )}

          {role === 'admin' && (
            <button
              onClick={() => handleRunEvaluation(item.model_id, releaseDate)}
              disabled={loading}
              style={{ width: '100%', padding: '8px', backgroundColor: '#0073bb', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>
              ⚡ Re-Run Golden Suite Benchmark
            </button>
          )}
        </div>
      </div>
    );
  };


  const __ctx = {
    handleEvalJobDone, pushVmPick, setPushVmPick, pushOpts, setPushOpts, handleVmFlip,
    token, setToken, role, setRole, privileges, setPrivileges,
    allPrivileges, setAllPrivileges, adminConfig, setAdminConfig, tfTestResult, setTfTestResult,
    adminUsers, setAdminUsers, newUser, setNewUser, username, setUsername,
    loginUser, setLoginUser, loginPass, setLoginPass, sidebarCollapsed, setSidebarCollapsed,
    mainNav, setMainNav, activeTab, setActiveTab, searchQuery, setSearchQuery,
    echostarCatalog, setEchostarCatalog, otherCatalog, setOtherCatalog, discoveredModels, setDiscoveredModels,
    marketScanDays, setMarketScanDays, suitesCatalog, setSuitesCatalog, showAddSuiteModal, setShowAddSuiteModal,
    newSuiteData, setNewSuiteData, modelId, setModelId, scorecard, setScorecard,
    agentLogs, setAgentLogs, loading, setLoading, statusMsg, setStatusMsg,
    selectedJustificationModel, setSelectedJustificationModel, evalResultModel, setEvalResultModel, evalResultLoading, setEvalResultLoading,
    vmStatus, setVmStatus, selectedVmMapping, setSelectedVmMapping, selectedDeployMode, setSelectedDeployMode,
    expandedDetails, setExpandedDetails, sortConfig, setSortConfig, colFilters, setColFilters,
    batchResult, setBatchResult, selectedLogComponent, setSelectedLogComponent, fetchedLogs, setFetchedLogs,
    can, handleLogin, handleLogout, getAuthHeaders, fetchCatalogs, fetchSuitesCatalog,
    deleteSuite, fetchSystemLogs, scanArtificialAnalysisMarket, handlePromoteToEchostarCatalog, handleTrueFoundrySync, handleDemoteToOtherCatalog,
    handleImportAndEvaluateFromMarket, handleSyncToArtifactory, handleRunEvaluation, handleRowEvaluate, handleVmPush, handleVmPull,
    handleChallengerAdd, handleChallengerRemove,
    fetchAdminConfig, saveAdminConfig, handleTfTest, fetchAdminUsers, saveUser, deleteUser,
    countEvaluated, handleEvaluateAllModels, handleAddGoldenSuite, toggleDetails, filterBySearch, filteredEchostar,
    filteredOther, filteredDiscovered, filteredSuites, getGovernanceAnalysis, scNum, fmtCell,
    costPerMillion, normalizeScorecardItem, fmtCostPerMCell, isSelfHostableModel, renderModelTable, renderModelScorecard,
    bedrockBadge, bedrockStateOf,
    tfEnabled, tfPulledAt, pullTruefoundryModels, tfEnabledMatch,
  };
  return <AppContext.Provider value={__ctx}>{children}</AppContext.Provider>;
}
